/* =====================================================================
   NFL MUNICH GAME 2026 — Plan interactif de l'Allianz Arena (config NFL)
   ---------------------------------------------------------------------
   Géométrie procédurale : 3 anneaux (Unterrang / Mittelrang / Oberrang)
   en super-ellipse, découpés en blocs de longueur d'arc égale.
   Chaque bloc = 1 variante Shopify (option « Bloc ») avec son stock.
   Utilisable dans le navigateur (window.MGStadium) et en Node
   (tools/gen-blocks.mjs) pour garantir la cohérence plan ⇄ catalogue.
   ===================================================================== */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.MGStadium = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const VB = { w: 1000, h: 740 };
  const C = { x: 500, y: 370 };
  const EXP = 3.4; // exposant de la super-ellipse (forme « stade » à coins arrondis)

  const RINGS = [
    { ring: 1, tier: 'Unterrang', level: 'Niveau 1', inA: 256, inB: 134, outA: 316, outB: 190, n: 28, start: 101, seats: 640 },
    { ring: 2, tier: 'Mittelrang', level: 'Niveau 2', inA: 326, inB: 200, outA: 376, outB: 246, n: 32, start: 201, seats: 520 },
    { ring: 3, tier: 'Oberrang', level: 'Niveau 3', inA: 388, inB: 258, outA: 452, outB: 318, n: 36, start: 301, seats: 900 },
  ];

  // Terrain : 120 yards (end zones incluses) x 53,3 yards
  const FIELD = { w: 440, h: 196 };
  const YD = FIELD.w / 120;

  const CAT_RULES = {
    1: { center: 'CAT1', wing: 'CAT1', corner: 'CAT3', end: 'CAT4' },
    2: { center: 'PRIME', wing: 'CAT2', corner: 'CAT3', end: 'CAT4' },
    3: { center: 'CAT5', wing: 'CAT5', corner: 'CAT6', end: 'CAT6' },
  };
  const VIEW = { center: 5, wing: 4, corner: 3, end: 3 };
  // Catégories épuisées (concept : forte demande). Source unique pour le plan, blocks.json et le CSV.
  const SOLD_OUT = ['PRIME', 'CAT1', 'CAT4', 'CAT6'];

  /* ---------- Maths ---------- */
  function sePoint(a, b, t) {
    const c = Math.cos(t), s = Math.sin(t), p = 2 / EXP;
    return [Math.sign(c) * a * Math.pow(Math.abs(c), p), Math.sign(s) * b * Math.pow(Math.abs(s), p)];
  }
  // Table de longueur d'arc — départ en haut au centre, sens horaire (repère SVG, y vers le bas)
  function arcTable(a, b, M) {
    M = M || 2048;
    const pts = [], len = [0];
    for (let i = 0; i <= M; i++) {
      const t = -Math.PI / 2 + (i / M) * Math.PI * 2;
      pts.push(sePoint(a, b, t));
      if (i) {
        const p0 = pts[i - 1], p1 = pts[i];
        len.push(len[i - 1] + Math.hypot(p1[0] - p0[0], p1[1] - p0[1]));
      }
    }
    return { pts: pts, len: len, total: len[M] };
  }
  function pointAt(tab, f) {
    f = ((f % 1) + 1) % 1;
    const target = f * tab.total;
    let lo = 0, hi = tab.len.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (tab.len[mid] < target) lo = mid; else hi = mid;
    }
    const seg = tab.len[hi] - tab.len[lo] || 1;
    const k = (target - tab.len[lo]) / seg;
    const a = tab.pts[lo], b = tab.pts[hi];
    return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];
  }
  function hash(n) {
    let t = (n * 2654435761) >>> 0;
    t ^= t >>> 15; t = Math.imul(t, 0x2c1b3c6d) >>> 0;
    t ^= t >>> 12; t = Math.imul(t, 0x297a2d39) >>> 0;
    t ^= t >>> 15;
    return (t >>> 0) / 4294967296;
  }
  const f1 = (n) => n.toFixed(1);
  function textOn(hex) {
    const h = (hex || '#888888').replace('#', '');
    const r = parseInt(h.slice(0, 2), 16) / 255, g = parseInt(h.slice(2, 4), 16) / 255, b = parseInt(h.slice(4, 6), 16) / 255;
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return L > 0.55 ? '#0b1220' : '#ffffff';
  }

  function zoneOf(mx, my) {
    const X = Math.abs(mx), Y = Math.abs(my);
    if (X <= 30 * YD) return 'center';      // entre les 20 yards
    if (X <= FIELD.w / 2) return 'wing';     // jusqu'au fond des end zones
    if (Y >= 118) return 'corner';
    return 'end';
  }
  function sideOf(mx, my, zone) {
    if (zone === 'center' || zone === 'wing') return my < 0 ? 'Ouest' : 'Est';
    if (zone === 'end') return mx < 0 ? 'Nord' : 'Sud';
    return (mx < 0 ? 'Nord' : 'Sud') + '-' + (my < 0 ? 'Ouest' : 'Est');
  }

  /* ---------- Blocs ---------- */
  let cache = null;
  // Stock réel (thème Shopify) : { "220": 145, ... } — les blocs absents sont considérés complets
  let stockOverride = null;
  function setStock(map) { stockOverride = map || null; cache = null; }
  function computeBlocks() {
    if (cache) return cache;
    const blocks = [];
    RINGS.forEach(function (r) {
      const inT = arcTable(r.inA, r.inB);
      const outT = arcTable(r.outA, r.outB);
      const midT = arcTable((r.inA + r.outA) / 2, (r.inB + r.outB) / 2);
      const gIn = 3 / inT.total, gOut = 3 / outT.total;
      for (let k = 0; k < r.n; k++) {
        const fc = k / r.n, fa = fc - 0.5 / r.n, fb = fc + 0.5 / r.n;
        const mid = pointAt(midT, fc);
        const zone = zoneOf(mid[0], mid[1]);
        const side = sideOf(mid[0], mid[1], zone);
        const id = r.start + k;
        const h = hash(id);
        const cap = Math.round(r.seats * (0.85 + hash(id * 7) * 0.3));
        const cat = CAT_RULES[r.ring][zone];
        const remaining = stockOverride
          ? Math.max(0, Number(stockOverride[String(id)]) || 0)
          : SOLD_OUT.indexOf(cat) > -1 || h < 0.08 ? 0 : Math.max(4, Math.round(cap * Math.pow(h, 1.6) * 0.42));
        // contour : courbe intérieure puis extérieure (inversée)
        const pts = [];
        const steps = 6;
        for (let i = 0; i <= steps; i++) pts.push(pointAt(inT, fa + gIn + (fb - fa - 2 * gIn) * i / steps));
        for (let i = steps; i >= 0; i--) pts.push(pointAt(outT, fa + gOut + (fb - fa - 2 * gOut) * i / steps));
        const d = 'M' + pts.map(function (p) { return f1(C.x + p[0]) + ' ' + f1(C.y + p[1]); }).join('L') + 'Z';
        blocks.push({
          block: String(id),
          ring: r.ring,
          tier: r.tier,
          level: r.level,
          zone: zone,
          side: side,
          bench: side.indexOf('Ouest') > -1 ? 'Lions' : side.indexOf('Est') > -1 ? 'Patriots' : null,
          cat: cat,
          view: Math.max(1, VIEW[zone] - (r.ring === 3 ? 1 : 0)),
          capacity: cap,
          remaining: remaining,
          x: +f1(C.x + mid[0]),
          y: +f1(C.y + mid[1]),
          d: d,
        });
      }
    });
    cache = blocks;
    return blocks;
  }

  function availability(blocks) {
    const cap = blocks.reduce(function (s, b) { return s + b.capacity; }, 0);
    const rem = blocks.reduce(function (s, b) { return s + b.remaining; }, 0);
    return cap ? rem / cap : 0;
  }
  function availLabel(ratio) {
    if (ratio <= 0) return { key: 'out', label: 'Complet' };
    if (ratio < 0.08) return { key: 'last', label: 'Dernières places' };
    if (ratio < 0.18) return { key: 'high', label: 'Forte demande' };
    return { key: 'ok', label: 'Disponible' };
  }

  /* ---------- Rendu SVG ---------- */
  function superPath(a, b) {
    const t = arcTable(a, b, 360);
    return 'M' + t.pts.map(function (p) { return f1(C.x + p[0]) + ' ' + f1(C.y + p[1]); }).join('L') + 'Z';
  }

  function fieldSVG(uid, o, mini) {
    const fx = C.x - FIELD.w / 2, fy = C.y - FIELD.h / 2, ez = 10 * YD;
    let s = '<g class="stad__field" aria-hidden="true">';
    s += '<rect x="' + fx + '" y="' + fy + '" width="' + FIELD.w + '" height="' + FIELD.h + '" rx="3" fill="url(#' + uid + '-turf)"/>';
    s += '<rect x="' + fx + '" y="' + fy + '" width="' + f1(ez) + '" height="' + FIELD.h + '" fill="' + o.homeColor + '" fill-opacity=".85"/>';
    s += '<rect x="' + f1(fx + FIELD.w - ez) + '" y="' + fy + '" width="' + f1(ez) + '" height="' + FIELD.h + '" fill="' + o.awayColor + '" fill-opacity=".95"/>';
    if (!mini) {
      for (let i = 0; i < 10; i += 2) {
        s += '<rect x="' + f1(fx + ez + i * 10 * YD) + '" y="' + fy + '" width="' + f1(10 * YD) + '" height="' + FIELD.h + '" fill="#fff" fill-opacity=".035"/>';
      }
      for (let yd = 0; yd <= 100; yd += 5) {
        const x = f1(fx + ez + yd * YD);
        const strong = yd === 0 || yd === 100 || yd === 50;
        s += '<line x1="' + x + '" y1="' + (fy + 3) + '" x2="' + x + '" y2="' + (fy + FIELD.h - 3) + '" stroke="#fff" stroke-opacity="' + (strong ? 0.7 : 0.28) + '" stroke-width="' + (strong ? 1.4 : 0.8) + '"/>';
      }
      for (let yd = 10; yd <= 90; yd += 10) {
        const n = yd <= 50 ? yd : 100 - yd;
        const x = f1(fx + ez + yd * YD);
        s += '<text x="' + x + '" y="' + (fy + 24) + '" class="stad__ydnum">' + n + '</text>';
        s += '<text x="' + x + '" y="' + (fy + FIELD.h - 15) + '" class="stad__ydnum">' + n + '</text>';
      }
      s += '<text class="stad__ez" transform="translate(' + f1(fx + ez / 2 + 7) + ' ' + C.y + ') rotate(-90)">' + o.home + '</text>';
      s += '<text class="stad__ez" transform="translate(' + f1(fx + FIELD.w - ez / 2 - 7) + ' ' + C.y + ') rotate(90)" fill="' + o.awayAccent + '">' + o.away + '</text>';
      // logo central (losanges bavarois)
      s += '<circle cx="' + C.x + '" cy="' + C.y + '" r="17" fill="none" stroke="#fff" stroke-opacity=".7" stroke-width="1.5"/>';
    }
    s += '<rect x="' + fx + '" y="' + fy + '" width="' + FIELD.w + '" height="' + FIELD.h + '" rx="3" fill="none" stroke="#fff" stroke-opacity="' + (mini ? 0.25 : 0.6) + '"/>';
    return s + '</g>';
  }

  /**
   * Rend le plan dans `el`.
   * @param {HTMLElement} el
   * @param {Object} opts { mode: 'full'|'mini', categories: {CODE:{color,name,price,label}}, highlight: 'CAT1', interactive: bool, selected: '112' }
   * @returns contrôleur { highlight(cat), filterRing(n), select(id), blocks, el }
   */
  function render(el, opts) {
    const o = Object.assign({
      mode: 'full', theme: 'light', categories: {}, highlight: null, interactive: true, selected: null,
      home: 'LIONS', away: 'PATRIOTS', homeColor: '#0076B6', awayColor: '#002244', awayAccent: '#ffffff',
    }, opts || {});
    const mini = o.mode === 'mini';
    const blocks = computeBlocks();
    const uid = 'st' + Math.random().toString(36).slice(2, 8);
    const color = function (cat) { return (o.categories[cat] && o.categories[cat].color) || '#8899aa'; };

    let s = '<svg class="stad__svg" viewBox="0 0 ' + VB.w + ' ' + VB.h + '" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="' +
      (o.ariaLabel || (mini ? 'Zone mise en évidence sur le plan du stade' : 'Plan interactif de l’Allianz Arena en configuration NFL')) + '">';
    s += '<defs>' +
      '<linearGradient id="' + uid + '-turf" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#2f7a43"/><stop offset="1" stop-color="#276a39"/></linearGradient>' +
      
      '<pattern id="' + uid + '-hatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="7" height="7" fill="' + (o.theme === 'dark' ? '#1d2840' : '#e4e8ee') + '"/><line x1="0" y1="0" x2="0" y2="7" stroke="' + (o.theme === 'dark' ? '#3a4660' : '#b9c1cc') + '" stroke-width="2"/></pattern>' +
      '<pattern id="' + uid + '-rauten" width="16" height="10" patternUnits="userSpaceOnUse"><rect width="16" height="10" fill="#071021"/><path d="M8 .8 L15.2 5 L8 9.2 L.8 5Z" fill="#3AA6FF" fill-opacity=".38"/></pattern>' +
      '</defs>';

    // Coque extérieure + façade en losanges (clin d'œil à l'enveloppe ETFE de l'Arena)
    const dark = o.theme === 'dark';
    s += '<path d="' + superPath(470, 338) + '" fill="' + (dark ? '#121b2d' : '#f2f4f7') + '" stroke="' + (dark ? 'rgba(255,255,255,.12)' : '#d0d5dd') + '" stroke-width="1.5"/>';
    s += '<path d="' + superPath(250, 128) + '" fill="' + (dark ? '#1f4a2c' : '#cfdccf') + '"/>';

    // Blocs
    s += '<g class="stad__blocks">';
    blocks.forEach(function (b) {
      const out = b.remaining === 0;
      const hl = !o.highlight || o.highlight === b.cat;
      let fill;
      if (mini) fill = hl ? color(b.cat) : (o.theme === 'dark' ? 'rgba(255,255,255,.14)' : '#d5dae2');
      else fill = out ? 'url(#' + uid + '-hatch)' : color(b.cat);
      s += '<path class="blk' + (out ? ' is-soldout' : '') + (mini && hl ? ' is-hl' : '') + '" d="' + b.d + '" fill="' + fill + '"' +
        ' stroke="' + (o.theme === 'dark' ? '#0b1220' : '#ffffff') + '" stroke-width="' + (mini ? 3 : 2) + '"' +
        ' style="--cat:' + color(b.cat) + '" data-block="' + b.block + '" data-cat="' + b.cat + '" data-ring="' + b.ring + '"' +
        (!mini && o.interactive ? ' role="button" aria-label="Bloc ' + b.block + ', ' + ((o.categories[b.cat] || {}).name || b.cat) + (out ? ', ' + ((o.t && o.t.sold_out) || 'complet') : '') + '"' : '') +
        '/>';
    });
    s += '</g>';

    if (!mini) {
      s += '<g class="stad__labels" aria-hidden="true">';
      blocks.forEach(function (b) {
        const tc = b.remaining === 0 ? (o.theme === 'dark' ? 'rgba(255,255,255,.45)' : '#8a94a3') : textOn(color(b.cat));
        s += '<text x="' + b.x + '" y="' + (b.y + 4.5) + '" class="stad__lbl" fill="' + tc + '" data-lbl="' + b.block + '">' + b.block + '</text>';
      });
      s += '</g>';
    }
    s += fieldSVG(uid, o, mini);
    s += '</svg>';

    el.classList.add('stad', mini ? 'stad--mini' : 'stad--full');
    el.innerHTML = s + (mini ? '' : '<div class="stad__tip" role="status" aria-live="polite" hidden></div>');

    const svg = el.querySelector('svg');
    const paths = Array.prototype.slice.call(el.querySelectorAll('.blk'));
    const byId = {};
    blocks.forEach(function (b) { byId[b.block] = b; });

    const api = {
      el: el, blocks: blocks, byId: byId,
      highlight: function (cat) {
        el.classList.toggle('has-hl', !!cat);
        paths.forEach(function (p) { p.classList.toggle('is-hl', !!cat && p.dataset.cat === cat); });
      },
      filterRing: function (ring) {
        el.classList.toggle('has-ring', !!ring);
        paths.forEach(function (p) { p.classList.toggle('is-dim', !!ring && +p.dataset.ring !== +ring); });
      },
      select: function (id, silent) {
        paths.forEach(function (p) { p.classList.toggle('is-selected', p.dataset.block === String(id)); });
        el.querySelectorAll('.stad__lbl').forEach(function (t) { t.classList.toggle('is-selected', t.dataset.lbl === String(id)); });
        if (!silent && byId[id]) el.dispatchEvent(new CustomEvent('mg:block-select', { bubbles: true, detail: byId[id] }));
      },
    };

    if (!mini && o.interactive) {
      const tip = el.querySelector('.stad__tip');
      // Textes traduisibles (thème Shopify) avec repli français (prototype)
      const T = Object.assign({ block: 'Bloc', sold_out: 'Complet', only_left: 'Plus que {n} places', seats_available: '{n} places dispo.', side_of: 'Côté {team}', from: 'dès {p}' }, o.t || {});
      const fill = function (str, k, v) { return String(str).replace('{' + k + '}', v); };
      const sideName = o.sideName || function (x) { return x; };
      const money = o.money || function (v) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v); };
      let active = null;
      function showTip(p, evt) {
        const b = byId[p.dataset.block];
        const c = o.categories[b.cat] || {};
        const av = b.remaining === 0 ? T.sold_out : b.remaining < 40 ? fill(T.only_left, 'n', b.remaining) : fill(T.seats_available, 'n', b.remaining);
        tip.innerHTML =
          '<div class="stad__tip-head"><span class="swatch" style="--cat:' + color(b.cat) + '"></span><strong>' + T.block + ' ' + b.block + '</strong><span class="stad__tip-cat">' + (c.label || b.cat) + '</span></div>' +
          '<div class="stad__tip-meta">' + b.tier + ' · ' + sideName(b.side) + (b.bench ? ' · ' + fill(T.side_of, 'team', b.bench) : '') + '</div>' +
          '<div class="stad__tip-foot"><span class="stad__tip-price">' + (c.price ? fill(T.from, 'p', money(c.price)) : '') + '</span><span class="stad__tip-av' + (b.remaining === 0 ? ' is-out' : b.remaining < 40 ? ' is-low' : '') + '">' + av + '</span></div>';
        tip.hidden = false;
        const r = el.getBoundingClientRect();
        let x, y;
        if (evt && evt.clientX) { x = evt.clientX - r.left; y = evt.clientY - r.top; }
        else { const bb = p.getBoundingClientRect(); x = bb.left + bb.width / 2 - r.left; y = bb.top - r.top; }
        const tw = tip.offsetWidth;
        x = Math.min(Math.max(x, tw / 2 + 8), r.width - tw / 2 - 8);
        tip.style.left = x + 'px';
        tip.style.top = y + 'px';
        tip.classList.toggle('is-below', y < tip.offsetHeight + 24);
      }
      svg.addEventListener('pointermove', function (e) {
        const p = e.target.closest('.blk');
        if (!p) { if (active) { active.classList.remove('is-active'); active = null; } tip.hidden = true; return; }
        if (active !== p) {
          if (active) active.classList.remove('is-active');
          active = p; p.classList.add('is-active');
          el.dispatchEvent(new CustomEvent('mg:block-hover', { bubbles: true, detail: byId[p.dataset.block] }));
        }
        if (e.pointerType === 'mouse') showTip(p, e);
      });
      svg.addEventListener('pointerleave', function () {
        if (active) active.classList.remove('is-active');
        active = null; tip.hidden = true;
        el.dispatchEvent(new CustomEvent('mg:block-hover', { bubbles: true, detail: null }));
      });
      svg.addEventListener('click', function (e) {
        const p = e.target.closest('.blk');
        if (!p) return;
        if (p.classList.contains('is-soldout')) { showTip(p); return; }
        api.select(p.dataset.block);
        if (e.pointerType !== 'mouse') showTip(p);
      });
    }

    if (o.highlight && !mini) api.highlight(o.highlight);
    if (o.selected) api.select(o.selected, true);
    return api;
  }

  return {
    VB: VB, RINGS: RINGS, CAT_RULES: CAT_RULES, SOLD_OUT: SOLD_OUT,
    computeBlocks: computeBlocks,
    setStock: setStock,
    availability: availability,
    availLabel: availLabel,
    render: render,
  };
});
